import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { create } from 'zustand'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth'
import { http } from '@/lib/http'
import type { PayWebAuthorizeBo } from '@/types/api'
import {
  blockWarmup,
  cleanReturnPath,
  dismissPaymentRestoration,
  consumePaymentIntent,
  markWarmupAttempt,
  paymentReturnTicket,
  peekReturnIntent,
  PaymentIntentError,
  type PaymentIntent,
} from './intent'

interface AuthorizationState {
  authorizing: boolean
  returnStarted: boolean
  cancelled: boolean
  pending: PaymentIntent | null
}
export const usePaymentAuthorization = create<AuthorizationState>(() => ({
  authorizing: Boolean(paymentReturnTicket),
  returnStarted: false,
  cancelled: false,
  pending: null,
}))

/** 在原业务布局内兑换结果，不渲染授权中间页，也不在此处发起付款。 */
export function usePaymentAuthorizationReturn() {
  const navigate = useNavigate()
  const active = useRef(false)
  useEffect(() => {
    active.current = true
    if (!paymentReturnTicket || usePaymentAuthorization.getState().returnStarted)
      return () => {
        active.current = false
      }
    usePaymentAuthorization.setState({ returnStarted: true })
    const token = useAuthStore.getState().accessToken
    const preview = token ? peekReturnIntent(token) : null
    navigate(cleanReturnPath(token), { replace: true })
    if (!token || !preview) {
      usePaymentAuthorization.setState({ authorizing: false })
      if (token) blockWarmup(token)
      toast.info('支付进度已失效，请重新发起支付')
      return () => {
        active.current = false
      }
    }
    markWarmupAttempt(token)
    void (async () => {
      try {
        const returnUrl = await http.post<string>(
          '/app/pay/wechat/web/authorize',
          { ticket: paymentReturnTicket } satisfies PayWebAuthorizeBo,
          { silentError: !preview.target },
        )
        if (!active.current || useAuthStore.getState().accessToken !== token) return
        const intent = consumePaymentIntent(returnUrl, token)
        markWarmupAttempt(token)
        const expected = new URL(intent.returnUrl)
        const actual = new URL(window.location.href)
        if (expected.pathname !== actual.pathname || expected.search !== actual.search) return
        if (!usePaymentAuthorization.getState().cancelled) {
          usePaymentAuthorization.setState({ pending: intent.target ? intent : null })
        }
      } catch (error) {
        blockWarmup(token)
        if (active.current && preview.target && error instanceof PaymentIntentError)
          toast.error(error.message)
      } finally {
        usePaymentAuthorization.setState({ authorizing: false })
      }
    })()
    return () => {
      active.current = false
    }
  }, [navigate])
}

/** 用户关闭恢复的弹窗或主动改选商品时，取消旧的自动续付意图。 */
export function cancelReturnedPayment() {
  dismissPaymentRestoration()
  usePaymentAuthorization.setState({ pending: null, cancelled: true })
}
